import { useMemo } from 'react'

import { useFetchExternalInvoices } from '@features/invoices/hooks/useFetchExternalInvoices'
import { InvoiceStatus } from '@features/invoicesLegacy/enums/invoiceStatus'
import { Invoice } from '@features/invoicesLegacy/types/invoice'
import { isExternalInvoice } from '@features/paymentReconciliationModal/utils/isExternalInvoice'

import { useFetchInvoices } from '../../../routes/InvoicesList/hooks/useFetchInvoices'

interface UseFetchInvoicesForPaymentReconciliationResponse {
  invoices: Invoice[]
  isLoading: boolean
}

export const useFetchInvoicesForPaymentReconciliation = (
  subject: Invoice,
): UseFetchInvoicesForPaymentReconciliationResponse => {
  const isExternal = isExternalInvoice(subject)

  const { invoices: billyInvoices, isLoading: isLoadingBillyInvoices } = useFetchInvoices({
    contactId: subject?.contactId,
    currencyId: subject?.currencyId,
    enabled: !isExternal,
    status: InvoiceStatus.Unpaid,
  })

  const { invoices: externalInvoices, isLoading: isLoadingExternalInvoices } = useFetchExternalInvoices({
    contactId: subject?.contactId,
    currencyId: subject?.currencyId,
    enabled: isExternal,
    isPaid: false,
    isVoided: false,
  })

  const invoices = useMemo(() => {
    const fetchedInvoices = isExternal ? externalInvoices : billyInvoices

    if (!subject?.id) {
      return fetchedInvoices
    }

    if (fetchedInvoices.some((invoice) => invoice.id === subject.id)) {
      return fetchedInvoices
    }

    return [subject, ...fetchedInvoices]
  }, [billyInvoices, externalInvoices, isExternal, subject])

  return {
    invoices,
    isLoading: isExternal ? isLoadingExternalInvoices : isLoadingBillyInvoices,
  }
}
